import { UploadType } from "@/api/types";
import { ExclamationCircleOutlined, LeftOutlined } from "@ant-design/icons";
import { Col, Modal, Row, Space, Steps } from "antd";
import modal from "antd/es/modal";
import { ReactNode, useState } from "react";
import { TableProps } from "../Table/Table";
import ImportStep from "./ImportStep";
import "./Uploader.scss";
import { UploadStep } from "./UploadStep";
import useValidationMutations from "./useValidationMutations";
import ValidationStep, { UploadParameters } from "./ValidationStep";

export const uploadTypeFileName: Record<UploadType, string> = {
  location: "facilities",
  bom: "products",
  po_orders: "purchase-orders",
  utilities: "utilities",
  sale_orders: "sales-orders",
  inbound_transport: "inbound-transport",
  outbound_transport: "outbound-transport",
  employee_commute: "employee-commute",
  employee_business_travel: "employee-business_travel",
  product_details: "product-details",
  packaging: "packaging",
  logistics_packaging: "facility-logistics-packaging",
  facility_logistics_packaging: "logistics-packaging",
  goods_and_services: "goods-and-services",
  facility_goods_and_services: "facility-goods-and-services",
};

export const uploadTypeTitle: Record<UploadType, string> = {
  bom: "Upload BOM",
  utilities: "Upload utility data",
  po_orders: "Upload purchase orders",
  sale_orders: "Upload sales orders",
  inbound_transport: "Upload inbound transport data",
  outbound_transport: "Upload outbound transport data",
  location: "Upload facility address and category",
  employee_commute: "Upload employee commute data",
  employee_business_travel: "Upload employee business travel data",
  product_details: "Upload product details",
  packaging: "Upload packaging",
  logistics_packaging: "Upload logistics packaging",
  facility_logistics_packaging: "Upload logistics packaging in bulk",
  facility_goods_and_services: "Upload goods and services",
  goods_and_services: "Upload goods and services",
};

type Step = "upload" | "validate" | "import";

const stepNumbers = {
  upload: 0,
  validate: 1,
  import: 2,
};

export function FileUploaderModal({
  isOpen,
  setIsOpen,
  uploadType,
  uploadDataTableProps,
  uploadParameters,
  productIds,
  howItWorks,
  templateUrlFn,
  note,
}: {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  uploadType: UploadType;
  uploadDataTableProps: TableProps;
  uploadParameters?: UploadParameters;
  productIds?: string[];
  howItWorks?: ReactNode;
  templateUrlFn?: () => Promise<string>;
  note?: ReactNode;
}) {
  const [step, setStep] = useState<Step>("upload");
  const [s3FileName, setS3FileName] = useState<string>();
  const [userFileName, setUserFileName] = useState<string>();
  const [importUUID, setImportUUID] = useState<string>();
  const [validateUUID, setValidateUUID] = useState<string>();

  const reset = () => {
    setStep("upload");
    setS3FileName(undefined);
    setUserFileName(undefined);
  };

  const onCancel = () => {
    if (s3FileName && step === "validate") {
      modal.confirm({
        title: "Discard upload?",
        icon: <ExclamationCircleOutlined />,
        content: "You haven't saved your data. Are you sure you want to leave?",
        okText: "Yes, discard upload",
        cancelText: "No",
        onCancel: () => {},
        centered: true,
        okButtonProps: {
          type: "default",
        },
        cancelButtonProps: {
          type: "primary",
        },
        onOk: () => {
          onClose();
        },
      });
    } else {
      onClose();
    }
  };

  const { createUploadRequestMutation } = useValidationMutations({ validate: false });

  const onClose = () => {
    setIsOpen(false);
    reset();
  };

  const onImportFn = (importUUID: string) => {
    createUploadRequestMutation
      .mutateAsync({
        uploadData: true,
        uploadType,
        UUID: importUUID,
        ...uploadParameters,
      })
      .then((data) => {
        setImportUUID(data.data.result.UUID);
        setStep("import");
      });
  };

  const onValidateFn = (s3FileName: string, userFileName: string) => {
    createUploadRequestMutation
      .mutateAsync({
        uploadData: false,
        uploadType,
        fileName: s3FileName,
        ...uploadParameters,
      })
      .then((data) => {
        setS3FileName(s3FileName);
        setUserFileName(userFileName);
        setValidateUUID(data.data.result.UUID);
        setStep("validate");
      });
  };

  const steps = {
    upload: (
      <UploadStep
        uploadType={uploadType}
        onDoneFn={onValidateFn}
        howItWorks={howItWorks}
        templateUrlFn={templateUrlFn}
        productIds={productIds}
      />
    ),
    validate: validateUUID && userFileName && (
      <ValidationStep
        {...uploadDataTableProps}
        fileName={userFileName}
        uploadType={uploadType}
        onCancelFn={onCancel}
        onImportFn={onImportFn}
        productIds={productIds}
        userFileName={userFileName}
        uploadUUID={validateUUID}
        preparingImport={createUploadRequestMutation.isLoading}
      />
    ),
    import: importUUID && (
      <ImportStep
        {...uploadDataTableProps}
        UUID={importUUID}
      />
    ),
  };

  return (
    <Modal
      title={uploadTypeTitle[uploadType]}
      open={isOpen}
      onCancel={onCancel}
      footer={null}
      width={2500}
      className={"uploader"}
    >
      {note && <div style={{ marginTop: 20 }}>{note}</div>}
      <Row
        justify={"center"}
        gutter={[25, 25]}
        style={{ paddingBottom: 50, paddingTop: 50 }}
      >
        <Col
          span={2}
          pull={5}
        >
          <span
            onClick={reset}
            style={{
              display: step !== "validate" ? "none" : "inline",
              cursor: "pointer",
            }}
          >
            <Space>
              <LeftOutlined /> Back
            </Space>
          </span>
        </Col>
        <Col
          span={12}
          pull={1}
        >
          <Steps
            size="small"
            current={stepNumbers[step]}
            items={[
              {
                title: "Upload",
              },
              {
                title: "Validate",
              },
              {
                title: "Import",
              },
            ]}
          />
        </Col>
      </Row>
      <Row>
        <Col span={24}>{steps[step]}</Col>
      </Row>
    </Modal>
  );
}
